import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import useScrollDetection from "../../hooks/useScrollDetection";
import { HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useState } from "react";
import { FaStar, FaRegStar } from "react-icons/fa";

const validationSchema = yup.object({});

const commonStyles = {
  fontFamily: '"Montserrat", sans-serif',
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#d1d5db",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9ca3af",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    padding: "14px 12px",
    color: "#111827",
    fontSize: "14px",
    fontFamily: '"Montserrat", sans-serif',
    "&::placeholder": {
      color: "#CBCBCB",
      opacity: 1,
      fontFamily: '"Montserrat", sans-serif',
    },
  },
  "&.Mui-disabled": {
    backgroundColor: "#f9fafb",
    opacity: 0.7,
  },
};

const textFieldStyles = {
  backgroundColor: "white",
  borderRadius: "8px",
  color: "#111827",
  transition: "all 0.3s ease",
  ...commonStyles,
};

const errorTextFieldStyles = {
  ...textFieldStyles,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#ef4444",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
    borderWidth: "1px",
  },
};

const formHelperFieldStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "start",
  margin: "4px 0 0 0",
  fontSize: "12px",
  color: "#ef4444",
  fontFamily: '"Montserrat", sans-serif',
};

const ConsultationRating = () => {
  const navigate = useNavigate();
  const isScrolled = useScrollDetection(5);
  const [hoveredStar, setHoveredStar] = useState(0);

  const shortReviewOptions = [
    { id: "ramah", label: "Ramah" },
    { id: "jelas", label: "Jelas" },
    { id: "cepat", label: "Cepat" },
    { id: "teliti", label: "Teliti" },
  ];

  const getFieldStyles = (hasError) => {
    return hasError ? errorTextFieldStyles : textFieldStyles;
  };

  const formik = useFormik({
    initialValues: {
      star: 0,
      short_review: [],
      review_detail: "",
    },
    validationSchema,
    onSubmit: (value) => {
      console.log("Form values:", value);
    },
    validateOnMount: true,
  });

  const handleStarClick = (selectedStar) => {
    formik.setFieldValue("star", selectedStar);
  };

  const handleShortReviewClick = (optionId) => {
    const currentReviews = formik.values.short_review;
    let newReviews;

    if (currentReviews.includes(optionId)) {
      // Jika sudah ada, hapus dari array
      newReviews = currentReviews.filter((item) => item !== optionId);
    } else {
      // Jika belum ada, tambahkan ke array
      newReviews = [...currentReviews, optionId];
    }

    formik.setFieldValue("short_review", newReviews);
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= (hoveredStar || formik.values.star);

      stars.push(
        <button
          key={i}
          type="button"
          onClick={() => handleStarClick(i)}
          onMouseEnter={() => setHoveredStar(i)}
          onMouseLeave={() => setHoveredStar(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          {isFilled ? (
            <FaStar className="text-yellow-400" size={32} />
          ) : (
            <FaRegStar className="text-yellow-400" size={32} />
          )}
        </button>,
      );
    }
    return stars;
  };

  return (
    <div className="bg-white relative">
      <div className="flex flex-row justify-center h-screen">
        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md py-4 px-3 lg:p-8 w-full h-fit">
            <div
              className={`bg-white fixed z-[9999] top-0 left-0 px-4 py-3 w-full flex flex-col items-center transition-all duration-300 ${
                isScrolled ? "shadow-md rounded-b-3xl" : ""
              }`}
            >
              <div className="flex flex-col w-full gap-4">
                <div className="w-full flex items-center gap-2 lg:hidden text-xl">
                  <div
                    onClick={() => navigate("/")}
                    className="p-1 hover:bg-gray-200 rounded-full cursor-pointer"
                  >
                    <IoArrowBack className="text-gray-500" size={20} />
                  </div>
                  <p className="font-semibold text-slate-700">Sesi Selesai</p>
                </div>
              </div>
            </div>

            <div className="pt-12 pb-16 space-y-3">
              <div className="w-full rounded-xl border border-gray-300 bg-white flex flex-col p-3">
                <div className="flex items-center gap-2">
                  <img
                    src="https://thumbs.dreamstime.com/b/beautiful-woman-fresh-skin-face-closeup-portrait-isolated-white-33924549.jpg"
                    alt="Profile IMG"
                    className="w-10 h-10 rounded-full border border-secondary object-cover"
                  />
                  <div>
                    <h1 className="font-semibold text-slate-700 line-clamp-1 leading-tight mb-1">
                      Dr. Muhammad Wildan Firdaus Bulyan
                    </h1>
                    <div className="flex items-center gap-2 text-slate-500 text-md">
                      <p className="">Dokter Umum</p>
                      <p>|</p>
                      <p className="">SIP 12345678910</p>
                    </div>
                  </div>
                </div>
                <hr className="my-2" />
                <div className="text-slate-700 font-medium">
                  <p>
                    Terima kasih telah berkonsultasi dengan{" "}
                    <span className="font-semibold">
                      Dr. Muhammad Wildan Firdaus Bulyan
                    </span>
                  </p>
                </div>
              </div>

              <div className="w-full rounded-xl border border-gray-300 bg-white flex flex-col p-3">
                <div className="flex items-center gap-2">
                  <HiOutlineChatBubbleLeftRight
                    size={24}
                    className="text-secondary"
                  />
                  <h1 className="font-semibold text-slate-700">
                    Berikan penilaian untuk sesi ini
                  </h1>
                </div>
                <hr className="my-2" />

                <div className="mb-4 flex items-center justify-center my-2">
                  <div className="flex gap-1">{renderStars()}</div>
                </div>

                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {shortReviewOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleShortReviewClick(option.id)}
                        className={`px-3 py-1 rounded-full text-md font-medium transition-all duration-200 border ${
                          formik.values.short_review.includes(option.id)
                            ? "bg-secondaryActive text-secondary border-secondary"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-slate-700 mb-1">Ulasan (Optional)</p>
                  <TextField
                    placeholder="Masukkan detail ulasan"
                    value={formik.values.review_detail}
                    multiline
                    minRows={4}
                    maxRows={6}
                    onChange={(e) => {
                      formik.setFieldValue("review_detail", e.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    name="review_detail"
                    fullWidth
                    helperText={
                      formik.touched.review_detail &&
                      formik.errors.review_detail
                    }
                    error={
                      formik.touched.review_detail &&
                      Boolean(formik.errors.review_detail)
                    }
                    sx={{
                      ...getFieldStyles(
                        formik.errors.review_detail &&
                          formik.touched.review_detail,
                      ),
                      "& .MuiOutlinedInput-root": {
                        padding: 0,
                        borderRadius: "8px",
                        "& textarea": {
                          padding: "12px 14px",
                          fontFamily: '"Montserrat", sans-serif',
                          fontSize: "14px",
                          lineHeight: "1.5",
                        },
                      },
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>
              </div>

              <div className="w-full font-medium rounded-xl border border-gray-300 bg-white flex flex-col p-3">
                <div>
                  <h1 className="text-slate-700 font-semibold">Diagnosa:</h1>
                  <p className="text-slate-500 mt-1">
                    Demam akut, kemungkinan infeksi ringan
                  </p>
                </div>
                <hr className="my-2" />
                <div>
                  <h1 className="text-slate-700 font-semibold">Resep:</h1>
                  <div className="flex flex-col mt-1">
                    <div className="">
                      <p className="text-slate-700">Paracetamol</p>
                      <p className="text-sm text-slate-500">
                        3x sehari setelah makan
                      </p>
                    </div>
                    <div className="">
                      <p className="text-slate-700">Paracetamol</p>
                      <p className="text-sm text-slate-500">
                        3x sehari setelah makan
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-base fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl py-4 px-3 z-[99] max-h-[70vh] flex flex-col shadow-2xl">
        <button
          type="button"
          // onClick={formik.handleSubmit}
          onClick={() => navigate("/")}
          className="h-[40px] border text-white flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline transform duration-200 hover:scale-100 border-secondary bg-secondary disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-default"
        >
          Kembali ke Beranda
        </button>
      </div>
    </div>
  );
};

export default ConsultationRating;
